import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'
import { complementaryShelfStateAtom } from '@/features/shop/state/ComplementaryShelf/atoms'
import { useCMSParamsWithoutSlug } from '@/features/cms/services/CMS/hooks'
import { getComplementaryShelfQueryOptions } from '@/features/cms/services/CMS/queries'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'
import { type OnComplementaryShelfFetch } from '@/features/shop/components/ProductCard/types'

export const useComplementaryShelfExperiment = (hasSearchTerm: boolean) => {
  const { active: isSearchComplementaryShelfActive, track } = useExperiment(
    ExperimentType.SEARCH_COMPLEMENTARY_SHELF,
  )

  return { isEnabled: isSearchComplementaryShelfActive && hasSearchTerm, track }
}

export const useFetchComplementaryShelf = (hasSearchTerm: boolean) => {
  const { isEnabled: shouldShowComplementaryShelf, track: trackExperiment } =
    useComplementaryShelfExperiment(hasSearchTerm)
  const queryClient = useQueryClient()
  const cmsParams = useCMSParamsWithoutSlug()
  const { isEligibleForShelf } = useAtomValue(complementaryShelfStateAtom)
  const setComplementaryShelfState = useSetAtom(complementaryShelfStateAtom)

  const fetchComplementaryShelf = useCallback<OnComplementaryShelfFetch>(
    async (productId, productIndex, productName) => {
      if (!hasSearchTerm || !isEligibleForShelf) {
        return
      }

      // We want this to fire for any user who would be shown the shelf IF the experiment were on
      // This includes control users as long as all other conditions above are met
      trackExperiment()

      if (!shouldShowComplementaryShelf) {
        return
      }

      setComplementaryShelfState((prev) => ({
        ...prev,
        isEligibleForShelf: false,
      }))

      try {
        const shelfData = await queryClient.query(
          getComplementaryShelfQueryOptions({
            ...cmsParams,
            product_id: productId,
          }),
        )

        if (!shelfData.data.items?.length) {
          return
        }

        setComplementaryShelfState((prev) => ({
          ...prev,
          shelfDisplayData: { shelfData, productIndex, productName },
        }))
      } catch (e) {
        // no-op
      }
    },
    [
      hasSearchTerm,
      shouldShowComplementaryShelf,
      isEligibleForShelf,
      trackExperiment,
      setComplementaryShelfState,
      queryClient,
      cmsParams,
    ],
  )

  return fetchComplementaryShelf
}
